/**
 * News page translations (all 8 locales).
 *
 * Only UI strings live here - post titles/excerpts come from the `news`
 * Firestore collection (canonical Russian copy until per-locale fields are
 * added upstream).
 */

import type { LanguageMap } from '@models/i18n';

export interface NewsDict {
    back: string;
    title: string;
    subtitle: string;
    createNews: string;
    newsTitle: string;
    newsSlug: string;
    newsSlugHint: string;
    newsCategory: string;
    catGeneral: string;
    catMaintenance: string;
    newsImage: string;
    newsExcerpt: string;
    newsContent: string;
    newsTags: string;
    newsTagsHint: string;
    newsFeatured: string;
    newsLang: string;
    cancel: string;
    save: string;
    newsCreatedSuccess: string;
    newsCreateError: string;

    // Category filter labels (also used as badges on cards)
    catAll: string;
    catUpdates: string;
    catEvents: string;
    catContests: string;
    catAnnouncements: string;

    // Card + featured post UI
    featuredLabel: string;
    readMore: string;
    byAuthor: string;
    minRead: string;

    // Empty state / pagination
    emptyTitle: string;
    emptyDesc: string;
    loadMore: string;

    // Live-data UX states
    loadError: string;
}

export const newsTranslations: LanguageMap<NewsDict> = {
    RU: {
        back: '← На главную',
        title: 'Новости',
        subtitle: 'Свежие обновления, события и анонсы игры.',
        createNews: 'Создать новость',
        newsTitle: 'Заголовок',
        newsSlug: 'URL-идентификатор',
        newsSlugHint: 'Только латинские буквы, цифры и дефисы',
        newsCategory: 'Категория',
        catGeneral: 'Общее',
        catMaintenance: 'Технические работы',
        newsImage: 'URL изображения',
        newsExcerpt: 'Краткое описание',
        newsContent: 'Содержание',
        newsTags: 'Теги',
        newsTagsHint: 'До 10 тегов, разделенных запятыми',
        newsFeatured: 'Важная новость',
        newsLang: 'Язык',
        cancel: 'Отмена',
        save: 'Сохранить',
        newsCreatedSuccess: 'Новость успешно создана',
        newsCreateError: 'Ошибка при создании новости',
        catAll: 'Все',
        catUpdates: 'Обновления',
        catEvents: 'События',
        catContests: 'Конкурсы',
        catAnnouncements: 'Анонсы',
        featuredLabel: 'Главное',
        readMore: 'Обзор',
        byAuthor: 'Автор',
        minRead: 'мин чтения',
        emptyTitle: 'Скоро здесь появятся новости',
        emptyDesc: 'Мы готовим первые анонсы и обновления. Загляните позже.',
        loadMore: 'Показать ещё',
        loadError: 'Не удалось загрузить новости. Попробуйте обновить страницу.',
    },
    EN: {
        back: '← Back to home',
        title: 'News',
        subtitle: 'Latest updates, events and announcements.',
        createNews: 'Create News',
        newsTitle: 'Title',
        newsSlug: 'URL Slug',
        newsSlugHint: 'Only letters, numbers, and hyphens',
        newsCategory: 'Category',
        catGeneral: 'General',
        catMaintenance: 'Maintenance',
        newsImage: 'Image URL',
        newsExcerpt: 'Excerpt',
        newsContent: 'Content',
        newsTags: 'Tags',
        newsTagsHint: 'Up to 10 tags, separated by commas',
        newsFeatured: 'Featured News',
        newsLang: 'Language',
        cancel: 'Cancel',
        save: 'Save',
        newsCreatedSuccess: 'News created successfully',
        newsCreateError: 'Error creating news',
        catAll: 'All',
        catUpdates: 'Updates',
        catEvents: 'Events',
        catContests: 'Contests',
        catAnnouncements: 'Announcements',
        featuredLabel: 'Featured',
        readMore: 'Preview',
        byAuthor: 'By',
        minRead: 'min read',
        emptyTitle: 'News coming soon',
        emptyDesc: "We're preparing the first announcements and updates. Check back later.",
        loadMore: 'Load more',
        loadError: 'Failed to load news. Please refresh the page.',
    },
    DE: {
        back: '← Zur Startseite',
        title: 'News',
        subtitle: 'Aktuelle Updates, Events und Ankündigungen.',
        createNews: 'News erstellen',
        newsTitle: 'Titel',
        newsSlug: 'URL-Slug',
        newsSlugHint: 'Nur Buchstaben, Zahlen und Bindestriche',
        newsCategory: 'Kategorie',
        catGeneral: 'Allgemein',
        catMaintenance: 'Wartung',
        newsImage: 'Bild-URL',
        newsExcerpt: 'Auszug',
        newsContent: 'Inhalt',
        newsTags: 'Tags',
        newsTagsHint: 'Bis zu 10 Tags, durch Kommas getrennt',
        newsFeatured: 'Wichtige News',
        newsLang: 'Sprache',
        cancel: 'Abbrechen',
        save: 'Speichern',
        newsCreatedSuccess: 'News erfolgreich erstellt',
        newsCreateError: 'Fehler beim Erstellen der News',
        catAll: 'Alle',
        catUpdates: 'Updates',
        catEvents: 'Events',
        catContests: 'Wettbewerbe',
        catAnnouncements: 'Ankündigungen',
        featuredLabel: 'Featured',
        readMore: 'Vorschau',
        byAuthor: 'Von',
        minRead: 'Min Lesezeit',
        emptyTitle: 'News folgen in Kürze',
        emptyDesc: 'Wir bereiten die ersten Ankündigungen und Updates vor. Schau später wieder rein.',
        loadMore: 'Mehr laden',
        loadError: 'News konnten nicht geladen werden. Bitte aktualisiere die Seite.',
    },
    FR: {
        back: '← Retour à l\'accueil',
        title: 'Actualités',
        subtitle: 'Dernières mises à jour, événements et annonces.',
        createNews: 'Créer une actualité',
        newsTitle: 'Titre',
        newsSlug: 'Slug URL',
        newsSlugHint: 'Uniquement des lettres, chiffres et tirets',
        newsCategory: 'Catégorie',
        catGeneral: 'Général',
        catMaintenance: 'Maintenance',
        newsImage: 'URL de l\'image',
        newsExcerpt: 'Extrait',
        newsContent: 'Contenu',
        newsTags: 'Tags',
        newsTagsHint: 'Jusqu\'à 10 tags, séparés par des virgules',
        newsFeatured: 'Actualité importante',
        newsLang: 'Langue',
        cancel: 'Annuler',
        save: 'Enregistrer',
        newsCreatedSuccess: 'Actualité créée avec succès',
        newsCreateError: 'Erreur lors de la création de l\'actualité',
        catAll: 'Tout',
        catUpdates: 'Mises à jour',
        catEvents: 'Événements',
        catContests: 'Concours',
        catAnnouncements: 'Annonces',
        featuredLabel: 'À la une',
        readMore: 'Aperçu',
        byAuthor: 'Par',
        minRead: 'min de lecture',
        emptyTitle: 'Actualités à venir',
        emptyDesc: 'Nous préparons les premières annonces et mises à jour. Revenez plus tard.',
        loadMore: 'Charger plus',
        loadError: 'Impossible de charger les actualités. Actualisez la page.',
    },
    PL: {
        back: '← Powrót do strony głównej',
        title: 'Aktualności',
        subtitle: 'Najnowsze aktualizacje, wydarzenia i zapowiedzi.',
        createNews: 'Utwórz aktualność',
        newsTitle: 'Tytuł',
        newsSlug: 'Slug URL',
        newsSlugHint: 'Tylko litery, cyfry i myślniki',
        newsCategory: 'Kategoria',
        catGeneral: 'Ogólne',
        catMaintenance: 'Konserwacja',
        newsImage: 'URL obrazu',
        newsExcerpt: 'Zajawka',
        newsContent: 'Treść',
        newsTags: 'Tagi',
        newsTagsHint: 'Do 10 tagów, oddzielonych przecinkami',
        newsFeatured: 'Ważna aktualność',
        newsLang: 'Język',
        cancel: 'Anuluj',
        save: 'Zapisz',
        newsCreatedSuccess: 'Aktualność utworzona pomyślnie',
        newsCreateError: 'Błąd podczas tworzenia aktualności',
        catAll: 'Wszystkie',
        catUpdates: 'Aktualizacje',
        catEvents: 'Wydarzenia',
        catContests: 'Konkursy',
        catAnnouncements: 'Zapowiedzi',
        featuredLabel: 'Wyróżnione',
        readMore: 'Podgląd',
        byAuthor: 'Autor',
        minRead: 'min czytania',
        emptyTitle: 'Wkrótce pojawią się wiadomości',
        emptyDesc: 'Przygotowujemy pierwsze ogłoszenia i aktualizacje. Zajrzyj później.',
        loadMore: 'Pokaż więcej',
        loadError: 'Nie udało się załadować aktualności. Odśwież stronę.',
    },
    ES: {
        back: '← Volver al inicio',
        title: 'Noticias',
        subtitle: 'Últimas actualizaciones, eventos y anuncios.',
        createNews: 'Crear noticia',
        newsTitle: 'Título',
        newsSlug: 'Slug URL',
        newsSlugHint: 'Solo letras, números y guiones',
        newsCategory: 'Categoría',
        catGeneral: 'General',
        catMaintenance: 'Mantenimiento',
        newsImage: 'URL de imagen',
        newsExcerpt: 'Extracto',
        newsContent: 'Contenido',
        newsTags: 'Tags',
        newsTagsHint: 'Hasta 10 tags, separados por comas',
        newsFeatured: 'Noticia importante',
        newsLang: 'Idioma',
        cancel: 'Cancelar',
        save: 'Guardar',
        newsCreatedSuccess: 'Noticia creada exitosamente',
        newsCreateError: 'Error al crear noticia',
        catAll: 'Todas',
        catUpdates: 'Actualizaciones',
        catEvents: 'Eventos',
        catContests: 'Concursos',
        catAnnouncements: 'Anuncios',
        featuredLabel: 'Destacado',
        readMore: 'Vista previa',
        byAuthor: 'Por',
        minRead: 'min de lectura',
        emptyTitle: 'Pronto habrá noticias',
        emptyDesc: 'Estamos preparando los primeros anuncios y actualizaciones. Vuelva pronto.',
        loadMore: 'Cargar más',
        loadError: 'No se pudieron cargar las noticias. Actualice la página.',
    },
    CZ: {
        back: '← Zpět na hlavní',
        title: 'Novinky',
        subtitle: 'Nejnovější aktualizace, události a oznámení.',
        createNews: 'Vytvořit novinku',
        newsTitle: 'Název',
        newsSlug: 'URL slug',
        newsSlugHint: 'Pouze písmena, čísla a pomlčky',
        newsCategory: 'Kategorie',
        catGeneral: 'Obecné',
        catMaintenance: 'Údržba',
        newsImage: 'URL obrázku',
        newsExcerpt: 'Úryvek',
        newsContent: 'Obsah',
        newsTags: 'Tagy',
        newsTagsHint: 'Až 10 tagů, oddělených čárkami',
        newsFeatured: 'Důležitá novinka',
        newsLang: 'Jazyk',
        cancel: 'Zrušit',
        save: 'Uložit',
        newsCreatedSuccess: 'Novinka úspěšně vytvořena',
        newsCreateError: 'Chyba při vytváření novinky',
        catAll: 'Vše',
        catUpdates: 'Aktualizace',
        catEvents: 'Události',
        catContests: 'Soutěže',
        catAnnouncements: 'Oznámení',
        featuredLabel: 'Hlavní',
        readMore: 'Náhled',
        byAuthor: 'Autor',
        minRead: 'min čtení',
        emptyTitle: 'Novinky se brzy objeví',
        emptyDesc: 'Chystáme první oznámení a aktualizace. Zkuste to později.',
        loadMore: 'Načíst další',
        loadError: 'Nepodařilo se načíst novinky. Obnovte stránku.',
    },
    IT: {
        back: '← Torna alla home',
        title: 'Notizie',
        subtitle: 'Ultimi aggiornamenti, eventi e annunci.',
        createNews: 'Crea notizia',
        newsTitle: 'Titolo',
        newsSlug: 'Slug URL',
        newsSlugHint: 'Solo lettere, numeri e trattini',
        newsCategory: 'Categoria',
        catGeneral: 'Generale',
        catMaintenance: 'Manutenzione',
        newsImage: 'URL immagine',
        newsExcerpt: 'Estratto',
        newsContent: 'Contenuto',
        newsTags: 'Tag',
        newsTagsHint: 'Fino a 10 tag, separati da virgole',
        newsFeatured: 'Notizia importante',
        newsLang: 'Lingua',
        cancel: 'Annulla',
        save: 'Salva',
        newsCreatedSuccess: 'Notizia creata con successo',
        newsCreateError: 'Errore durante la creazione della notizia',
        catAll: 'Tutte',
        catUpdates: 'Aggiornamenti',
        catEvents: 'Eventi',
        catContests: 'Concorsi',
        catAnnouncements: 'Annunci',
        featuredLabel: 'In primo piano',
        readMore: 'Anteprima',
        byAuthor: 'Di',
        minRead: 'min di lettura',
        emptyTitle: 'Notizie in arrivo',
        emptyDesc: 'Stiamo preparando i primi annunci e aggiornamenti. Torna più tardi.',
        loadMore: 'Carica altro',
        loadError: 'Impossibile caricare le notizie. Aggiorna la pagina.',
    },

    UA: {
        back: '← На головну',
        title: 'Новини',
        subtitle: 'Свіжі оновлення, події та анонси гри.',
        createNews: 'Створити новину',
        newsTitle: 'Заголовок',
        newsSlug: 'URL-ідентифікатор',
        newsSlugHint: 'Тільки латинські літери, цифри та дефіси',
        newsCategory: 'Категорія',
        catGeneral: 'Загальне',
        catMaintenance: 'Технічні роботи',
        newsImage: 'URL зображення',
        newsExcerpt: 'Короткий опис',
        newsContent: 'Зміст',
        newsTags: 'Теги',
        newsTagsHint: 'До 10 тегів, розділених комами',
        newsFeatured: 'Важлива новина',
        newsLang: 'Мова',
        cancel: 'Скасувати',
        save: 'Зберегти',
        newsCreatedSuccess: 'Новину успішно створено',
        newsCreateError: 'Помилка при створенні новини',
        catAll: 'Всі',
        catUpdates: 'Оновлення',
        catEvents: 'Події',
        catContests: 'Конкурси',
        catAnnouncements: 'Анонси',
        featuredLabel: 'Головне',
        readMore: 'Огляд',
        byAuthor: 'Автор',
        minRead: 'хв читання',
        emptyTitle: 'Скоро тут з\'являться новини',
        emptyDesc: 'Ми готуємо перші анонси та оновлення. Завітайте пізніше.',
        loadMore: 'Показати ще',
        loadError: 'Не вдалося завантажити новини. Спробуйте оновити сторінку.',
    },
};
